import React, { useEffect } from 'react';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { RequiredIndicator } from '@/components/shared';
import VacationPeriodSellField from './VacationPeriodSellField';

interface VacationPeriodsProps {
    context: CollaboratorFormContext;
}

const VacationPeriods: React.FC<VacationPeriodsProps> = ({ context }) => {
    const { control, watch, setValue } = useFormContext();

    // Helper para verificar se campo deve aparecer
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Helper local para verificar se campos são obrigatórios
    const isRequired = (fieldName: string) => collaboratorSchemaHelpers.isFieldRequired(fieldName, context);

    // useFieldArray para gerenciar períodos de férias (ANTES do early return)
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'vacationPeriods'
    });

    // Watch para campos condicionais (ANTES do early return)
    const formData = watch();
    const vacationPeriods = formData.vacationPeriods || [];

    // Função para calcular data final baseada na data inicial e dias
    const calculateEndDate = React.useCallback((startDate: string, days: number): string => {
        if (!startDate || !days || days <= 0) return '';

        const start = new Date(startDate);
        const end = new Date(start);
        end.setDate(start.getDate() + days - 1); // -1 porque o primeiro dia conta

        return end.toISOString().split('T')[0];
    }, []);

    // Effect para calcular data final automaticamente (ANTES do early return)
    useEffect(() => {
        vacationPeriods.forEach((period: any, index: number) => {
            if (period.startDate && period.days && period.days > 0) {
                const endDate = calculateEndDate(period.startDate, period.days);
                if (endDate !== period.endDate) {
                    setValue(`vacationPeriods.${index}.endDate`, endDate);
                }
            }
        });
    }, [vacationPeriods, setValue, calculateEndDate]);

    // Early return se não deve mostrar neste contexto (DEPOIS dos hooks)
    if (!shouldShow('showVacationPeriods')) {
        return null;
    }

    // Handler para adicionar novo período - usar o contexto
    const handleAddPeriod = () => {
        append({
            id: `vacation_${Date.now()}`,
            startDate: '',
            days: 0,
            endDate: '',
            observations: '',
            hasSellDays: false
        });
    };

    // Handler para remover período - usar o contexto
    const handleRemovePeriod = (index: number) => {
        remove(index);
    };

    // Handler personalizado para mudança de data que calcula imediatamente
    const handleDateChange = (index: number, date: string) => {
        const currentPeriod = vacationPeriods[index];
        if (currentPeriod && currentPeriod.days && currentPeriod.days > 0) {
            const endDate = calculateEndDate(date, currentPeriod.days);
            setValue(`vacationPeriods.${index}.endDate`, endDate);
        }
    };

    // Handler personalizado para mudança de dias que calcula imediatamente
    const handleDaysChange = (index: number, days: number) => {
        const currentPeriod = vacationPeriods[index];
        if (currentPeriod && currentPeriod.startDate && days > 0) {
            const endDate = calculateEndDate(currentPeriod.startDate, days);
            setValue(`vacationPeriods.${index}.endDate`, endDate);
        }
    };

    return (
        <div className="space-y-4" data-testid="vacation-periods">
            {fields.map((field, index) => (
                <div key={field.id} className="grid grid-cols-1 md:grid-cols-[1fr_100px_1fr_2fr_auto_auto] gap-4 items-end p-2 border rounded-md">

                    {/* Data de Início */}
                    <div>
                        <label htmlFor={`vacation-start-${index}`} className="block text-sm font-medium text-gray-700">
                            Início
                            {isRequired('startDate') && <RequiredIndicator show={true} />}
                        </label>
                        <Controller
                            name={`vacationPeriods.${index}.startDate`}
                            control={control}
                            rules={{ required: isRequired('startDate') ? 'Data de início é obrigatória' : false }}
                            render={({ field, fieldState }) => (
                                <>
                                    <input
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            handleDateChange(index, e.target.value);
                                        }}
                                        type="date"
                                        id={`vacation-start-${index}`}
                                        className="input-primary w-full mt-1"
                                    />
                                    {fieldState.error && (
                                        <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                    )}
                                </>
                            )}
                        />
                    </div>

                    {/* Quantidade de Dias */}
                    <div>
                        <label htmlFor={`vacation-days-${index}`} className="block text-sm font-medium text-gray-700">
                            Dias
                            {isRequired('days') && <RequiredIndicator show={true} />}
                        </label>
                        <Controller
                            name={`vacationPeriods.${index}.days`}
                            control={control}
                            rules={{
                                required: isRequired('days') ? 'Quantidade de dias é obrigatória' : false,
                                min: { value: 1, message: 'Deve ser pelo menos 1 dia' },
                                max: { value: 30, message: 'Máximo 30 dias por período' }
                            }}
                            render={({ field, fieldState }) => (
                                <>
                                    <input
                                        {...field}
                                        value={field.value || ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Converter para número ou usar 0 se vazio
                                            const numericValue = value === '' ? 0 : parseInt(value, 10);
                                            field.onChange(numericValue);
                                            // Calcular data final imediatamente
                                            handleDaysChange(index, numericValue);
                                        }}
                                        type="number"
                                        id={`vacation-days-${index}`}
                                        min="1"
                                        max="30"
                                        className="input-primary w-full mt-1"
                                    />
                                    {fieldState.error && (
                                        <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                    )}
                                </>
                            )}
                        />
                    </div>

                    {/* Data Final (Calculada) */}
                    <div>
                        <label htmlFor={`vacation-end-${index}`} className="block text-sm font-medium text-gray-700">
                            Fim
                        </label>
                        <Controller
                            name={`vacationPeriods.${index}.endDate`}
                            control={control}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    type="date"
                                    id={`vacation-end-${index}`}
                                    readOnly
                                    className="input-primary w-full mt-1 bg-gray-100"
                                />
                            )}
                        />
                    </div>

                    {/* Observações */}
                    <div>
                        <label htmlFor={`vacation-obs-${index}`} className="block text-sm font-medium text-gray-700">
                            Observações
                        </label>
                        <Controller
                            name={`vacationPeriods.${index}.observations`}
                            control={control}
                            render={({ field, fieldState }) => (
                                <>
                                    <input
                                        {...field}
                                        type="text"
                                        id={`vacation-obs-${index}`}
                                        className="input-primary w-full mt-1"
                                        placeholder="Ex: Férias coletivas"
                                    />
                                    {fieldState.error && (
                                        <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                    )}
                                </>
                            )}
                        />
                    </div>

                    {/* Abono pecuniário */}
                    <VacationPeriodSellField index={index} />

                    {/* Botão Remover */}
                    <button
                        type="button"
                        onClick={() => handleRemovePeriod(index)}
                        className="flex items-center justify-center h-10 px-3 text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                        title="Remover período"
                        data-testid={`remove-vacation-${index}`}
                    >
                        <TrashIcon className="h-5 w-5" />
                    </button>
                </div>
            ))}

            {/* Botão Adicionar Período */}
            <div className="flex justify-start">
                <button
                    type="button"
                    onClick={handleAddPeriod}
                    className="flex items-center gap-2 px-4 py-2 text-indigo-600 border border-indigo-300 rounded-md hover:bg-indigo-50 transition-colors"
                    data-testid="add-vacation-period"
                >
                    <PlusIcon className="h-4 w-4" />
                    Adicionar Período de Férias
                </button>
            </div>

            {/* Mensagem quando não há períodos */}
            {fields.length === 0 && (
                <div className="text-center py-8 text-gray-500" data-testid="no-vacation-periods">
                    <p>Nenhum período de férias programado.</p>
                    <p className="text-sm">Clique em "Adicionar Período de Férias" para começar.</p>
                </div>
            )}
        </div>
    );
};

export default VacationPeriods; 